import React, { useMemo } from 'react';
import '../../../styles/PendingBets.css';
import PendingBetComponent from './PendingBetComponent.tsx';
import { formatNumbers, isFreePlayBet } from '../../../helpers/functions.tsx';
import { usePlayerMyBets } from '../../../hooks/usePlayerMyBets.ts';

const getBetDateMs = (bet: { betDate?: { seconds?: number } | string }): number => {
    if (bet.betDate && typeof bet.betDate === 'object' && bet.betDate.seconds) {
        return bet.betDate.seconds * 1000;
    }
    return new Date(bet.betDate as unknown as string).getTime();
};

const PendingBetsSkeleton = ({ rows = 4 }: { rows?: number }) => (
    <div className="pending-list pending-bets-skeleton" aria-busy="true" aria-label="Loading pending bets">
        {Array.from({ length: rows }, (_, index) => (
            <div className="pending-body pending-bets-skeleton__row" key={index}>
                <div className="pending-bets-skeleton__ticket" />
                <div className="pending-bets-skeleton__main">
                    <div className="pending-bets-skeleton__desc">
                        <span className="pending-bets-skeleton__logo" />
                        <div className="pending-bets-skeleton__text">
                            <span className="pending-bets-skeleton__line pending-bets-skeleton__line--title" />
                            <span className="pending-bets-skeleton__line pending-bets-skeleton__line--sub" />
                        </div>
                    </div>
                    <span className="pending-bets-skeleton__amount" />
                    <span className="pending-bets-skeleton__amount" />
                </div>
            </div>
        ))}
    </div>
);

function TablePendingBetsComponent() {
    const { bets: userBet, isLoading, userName } = usePlayerMyBets();

    const { count, totalBet, totalReward, sortedUserBets } = useMemo(() => {
        const sorted = [...userBet].sort((a, b) => getBetDateMs(b) - getBetDateMs(a));
        return {
            count: userBet.length,
            totalBet: userBet.reduce((sum, bet) => isFreePlayBet(bet) ? sum : sum + Number(bet.bet), 0),
            totalReward: userBet.reduce((sum, bet) => sum + (bet.reward ?? 0), 0),
            sortedUserBets: sorted,
        };
    }, [userBet]);

    if (!userName) {
        return null;
    }

    return (
        <div className='container-pending mt-lg-4'>
            <div className="normal pending-display-small">
                <span className="title-bet teko wagers-left pending-display-small">WAGERS. {count}</span>
                <div className="d-flex">
                    <div className="view-total">
                        <span className="title-bet wagers-left pending-display-small">TOTAL: </span>
                    </div>
                    <div className="view-total">
                        <span className="teko rewards-content pending-display-small">$ {formatNumbers(totalBet)}</span>
                    </div>
                    <div className="view-total">
                        <span className="teko bets-content pending-display-small">$ {formatNumbers(totalReward)}</span>
                    </div>
                </div>
            </div>

            <div className='title-results' >
                <span className='teko title-container pending-display-small'>SPREAD / ML </span>
                <span className='teko title-container pending-display-small'>TOTAL / TT </span>
            </div>

            <div className='header-pending'>
                <div className='description-container'>
                    <span className='table-letters'>DESCRIPTION</span>
                </div>
                <div className='risk-container'>
                    <span className='table-letters'>RISK</span>
                </div>
                <div className='reward-container'>
                    <span className='table-letters'>REWARD</span>
                </div>
            </div>

            {isLoading && sortedUserBets.length === 0 ? (
                <PendingBetsSkeleton />
            ) : sortedUserBets.length > 0 ? (
                <div className="pending-list ">
                    {sortedUserBets.map((bet) => (
                        <div className="pending-body" key={bet.ticket ?? `${bet.betDate}-${bet.gameId}`}>
                            <PendingBetComponent {...bet} />
                        </div>
                    ))}
                </div>
            ) : null}

            <div className="container-bet">
                <div className="view-total">
                    <span className="title-bet">Total Risk: </span>
                    <span className="teko rewards-content">$ {formatNumbers(totalBet)}</span>
                </div>
                <div className="view-total">
                    <span className="title-bet">Total Win: </span>
                    <span className="teko bets-content">$ {formatNumbers(totalReward)}</span>
                </div>
            </div>
        </div>
    );
}

export default TablePendingBetsComponent;
